import { useEffect, useRef, useState } from "react";
import { Animated, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { SourcePhotoButton } from "./SourcePhotoButton";
import { SourcePhotoViewer } from "./SourcePhotoViewer";

import type { DraftSourceImage, Medication } from "@/lib/api";
import { fetchMedicationImages } from "@/lib/api";
import { colors, fonts, radius, shadows } from "@/lib/theme";

interface Props {
    medication: Medication;
    /** The visit these photos hang off — the images endpoint is scoped to it. */
    visitId: string;
    onPress: () => void;
    isNew?: boolean;
}

export function SavedItemCard({ medication, visitId, onPress, isNew }: Props) {
    const fadeAnim = useRef(new Animated.Value(isNew ? 0 : 1)).current;
    const slideAnim = useRef(new Animated.Value(isNew ? 20 : 0)).current;
    const glowAnim = useRef(new Animated.Value(isNew ? 1 : 0)).current;

    useEffect(() => {
        if (isNew) {
            Animated.parallel([
                Animated.timing(fadeAnim, {
                    toValue: 1,
                    duration: 400,
                    useNativeDriver: true
                }),
                Animated.timing(slideAnim, {
                    toValue: 0,
                    duration: 400,
                    useNativeDriver: true
                })
            ]).start();

            Animated.sequence([
                Animated.delay(200),
                Animated.timing(glowAnim, {
                    toValue: 0,
                    duration: 1500,
                    useNativeDriver: false
                })
            ]).start();
        }
    }, [isNew, fadeAnim, slideAnim, glowAnim]);

    // Build details line: Strength · Ordered dose · Frequency.
    // Route is deliberately NOT here — it already renders as the badge beside
    // the medication name, and pushing it in again both duplicated it and made
    // the row one item wider than the layout could take.
    const details: string[] = [];
    const strength = medication.strength ?? medication.dosage;
    const orderedDose = medication.orderedDose;
    if (strength) {
        details.push(strength);
    }
    if (orderedDose) {
        details.push(orderedDose);
    }
    if (medication.frequency) {
        details.push(medication.frequency);
    }

    const icdCodes = medication.diagnosisCodes ?? [];

    // Source photos, same as the draft card offers — saving does not make the
    // label any less worth checking. `hasSourceImages` comes down on the list so
    // the icon only appears where there is something behind it; fetching stays
    // lazy because the URLs are signed and short-lived.
    const [imagesOpen, setImagesOpen] = useState(false);
    const [images, setImages] = useState<DraftSourceImage[]>([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [imagesError, setImagesError] = useState<string | null>(null);

    const openSourceImages = async () => {
        setImagesOpen(true);
        if (images.length > 0 || imagesLoading) {
            return;
        }
        setImagesLoading(true);
        setImagesError(null);
        try {
            const result = await fetchMedicationImages(visitId, String(medication.id));
            setImages(result.images);
            if (result.images.length === 0) {
                setImagesError("Those photos are no longer available.");
            }
        } catch {
            setImagesError("Could not load the photos. Please try again.");
        } finally {
            setImagesLoading(false);
        }
    };

    const borderColor = glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [colors.border, colors.teal400]
    });

    return (
        <Animated.View
            style={{
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }],
                borderColor,
                borderWidth: 1.5,
                borderRadius: 18,
                marginBottom: 10,
                backgroundColor: colors.surface,
                ...shadows.sm
            }}
        >
            <TouchableOpacity style={ss.card} onPress={onPress} activeOpacity={0.7}>
                {/* Header: Name + Route badge + SAVED badge */}
                <View style={ss.header}>
                    <View style={ss.nameRow}>
                        <Text style={ss.name}>{medication.medicationName}</Text>
                        {medication.route && (
                            <View style={ss.routeBadge}>
                                <Text style={ss.routeText}>{medication.route}</Text>
                            </View>
                        )}
                        {medication.isSupplement && (
                            <View style={ss.supplementBadge}>
                                <Text style={ss.supplementText}>SUPPLEMENT</Text>
                            </View>
                        )}
                    </View>
                    <View style={ss.headerActions}>
                        {medication.hasSourceImages && (
                            <SourcePhotoButton
                                onPress={openSourceImages}
                                count={images.length || 1}
                                accessibilityLabel="Show the photos this medication was read from"
                            />
                        )}
                        <View style={ss.savedBadge}>
                            <Text style={ss.savedText}>SAVED</Text>
                        </View>
                    </View>
                </View>

                {/* Details: Strength · Ordered dose · Frequency */}
                {details.length > 0 && (
                    <View style={ss.detailsRow}>
                        {details.map((detail, index) => (
                            <View key={`${detail}-${index}`} style={ss.detailItem}>
                                {index > 0 && <Text style={ss.dot}>{"\u00B7"}</Text>}
                                <Text style={ss.detailText}>{detail}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {/* ICD chips — pill style matching DraftCard */}
                {icdCodes.length > 0 && (
                    <View style={ss.icdRow}>
                        {icdCodes.map((icd) => (
                            <View key={icd.icdCode} style={ss.icdChip}>
                                <Text style={ss.icdChipText} numberOfLines={1}>
                                    {icd.description || icd.icdCode}
                                </Text>
                            </View>
                        ))}
                    </View>
                )}
            </TouchableOpacity>

            {/* Outside the TouchableOpacity: the viewer is full-screen, and
                nesting it inside the card's press target would put the photo's
                own pan and pinch gestures underneath the card's onPress. */}
            <SourcePhotoViewer
                visible={imagesOpen}
                onClose={() => setImagesOpen(false)}
                title={medication.medicationName}
                images={images}
                loading={imagesLoading}
                error={imagesError}
            />
        </Animated.View>
    );
}

const ss = StyleSheet.create({
    card: {
        borderRadius: 18,
        padding: 14
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 6
    },
    nameRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 6,
        flex: 1
    },
    name: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    routeBadge: {
        backgroundColor: "#EDE9FE",
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 6
    },
    routeText: {
        fontSize: 10,
        fontFamily: fonts.semiBold,
        color: "#7C3AED"
    },
    supplementBadge: {
        backgroundColor: "#FFFBEB",
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: radius.full
    },
    supplementText: {
        fontSize: 9,
        fontFamily: fonts.bold,
        color: "#B45309",
        textTransform: "uppercase",
        letterSpacing: 0.5
    },
    headerActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    savedBadge: {
        backgroundColor: colors.teal50,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        marginLeft: 8
    },
    savedText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.teal600,
        textTransform: "uppercase",
        letterSpacing: 0.5
    },
    detailsRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        // Wrap instead of compress. Without this the row squeezes its items to
        // fit one line, and a short value like "Oral" collapses to a
        // one-character-per-line column ("O / r / a / l").
        flexWrap: "wrap",
        gap: 6,
        marginBottom: 4
    },
    detailItem: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 6,
        // Never squeeze an item below its text width; overflow moves to the
        // next line via the row's flexWrap.
        flexShrink: 0,
        maxWidth: "100%"
    },
    detailText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 16,
        flexShrink: 1
    },
    dot: {
        fontSize: 10,
        color: colors.textTertiary,
        lineHeight: 16
    },
    icdRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 5,
        marginTop: 6
    },
    icdChip: {
        paddingHorizontal: 11,
        paddingVertical: 5,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: "#CCFBEF",
        backgroundColor: "#F0FDF9"
    },
    icdChipText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: "#14957D"
    }
});
